import { BaseApi } from '@/apis/base.api';
import { moduleDefectApi } from '@/apis/module.defect.api';
import { extractWgs84Point } from '@/utils/defect/extractWgs84Point';
import { syncDefectCoordsAdjustMinimal } from '@/services/defect/defectCoordsAdjustSync';
import { unwrapServiceContent } from '@/utils/defect/unwrapServiceContent';
import {
  logDefectModuleLink,
  logDefectModuleLinkComparison,
  summarizeDefectModuleLinkState
} from '@/utils/debug/defectModuleLinkDebug';

function extractEntity(response) {
  return unwrapServiceContent(response?.data ?? response);
}

export function formatNearModuleLabel(module, fallbackPrefix = 'Module') {
  if (!module) {
    return '';
  }
  if (module.moduleId != null && module.moduleId !== '') {
    return String(module.moduleId);
  }
  if (module.id != null) {
    return `${fallbackPrefix} #${module.id}`;
  }
  return '';
}

/**
 * 서버가 저장한 defectLocation 기준으로 PATCH coords를 호출해 solar_module_seq를 연결한다.
 * GET 이 403 으로 실패해도 adjustDefectLocation(PATCH) 자체가 서버에서 모듈을 할당하므로
 * 좌표 정보만 PATCH 로 보낸 후 동기화한다.
 * @param {number | string} defectId
 * @param {import('vuex').Store} [store]
 * @param {{
 *   trigger?: string,
 *   thermalImageId?: number | string | null,
 *   defectShape?: object | null
 * }} [options]
 */
export async function syncDefectModuleLinkFromServerLocation(defectId, store, options = {}) {
  if (defectId == null || defectId === '') {
    return null;
  }

  const trigger = options.trigger ?? 'syncDefectModuleLinkFromServerLocation';
  const thermalImageId =
    options.thermalImageId ??
    store?.getters?.['dashboard/getSelectedThermalImageId'] ??
    null;

  logDefectModuleLink('start', {
    trigger,
    defectId,
    thermalImageId,
    hasDefectShape: !!options.defectShape
  });

  let point = null;
  let defectBefore = null;
  let nearModules = [];

  try {
    const response = await BaseApi.get(`/moduleDefects/${defectId}`);
    defectBefore = extractEntity(response);
    point = extractWgs84Point(defectBefore?.defectLocation);

    logDefectModuleLink('get-before-patch', {
      defectId,
      ...summarizeDefectModuleLinkState(defectBefore)
    });
  } catch (error) {
    logDefectModuleLink('get-before-patch-failed', {
      defectId,
      message: error?.message ?? String(error)
    });
  }

  if (!point) {
    logDefectModuleLink('abort-no-defect-location', { defectId, trigger });
    return null;
  }

  if (thermalImageId != null && options.defectShape) {
    try {
      nearModules = await moduleDefectApi.findSolarModulesNearDefect(
        thermalImageId,
        options.defectShape
      );
      logDefectModuleLink('near-modules-from-defect-shape', {
        defectId,
        thermalImageId,
        patchPoint: point,
        nearModuleCount: nearModules.length,
        note:
          'AI 재요청과 무관 — 픽셀 박스+imgPosCoords 기준 2m 이내 모듈. PATCH findContainingPoint는 폴리곤 내부만.'
      });
    } catch (error) {
      logDefectModuleLink('near-modules-failed', {
        defectId,
        thermalImageId,
        message: error?.message ?? String(error)
      });
    }
  }

  logDefectModuleLink('patch-coords-request', {
    defectId,
    lon: point.lon,
    lat: point.lat,
    serverSolarModuleSeqBefore: summarizeDefectModuleLinkState(defectBefore).solarModuleSeq
  });

  await moduleDefectApi.adjustDefectLocation(defectId, {
    lon: point.lon,
    lat: point.lat
  });

  let mergedDetail = null;
  if (store) {
    mergedDetail = await syncDefectCoordsAdjustMinimal(store, defectId, point.lon, point.lat);
  }

  let defectAfter = mergedDetail;
  if (!defectAfter) {
    try {
      defectAfter = await moduleDefectApi.getDefectById(defectId);
    } catch (error) {
      logDefectModuleLink('get-after-patch-failed', {
        defectId,
        message: error?.message ?? String(error)
      });
    }
  }

  logDefectModuleLinkComparison(store, defectId, {
    trigger,
    thermalImageId,
    before: defectBefore,
    after: defectAfter,
    patchPoint: point,
    nearModules
  });

  return point;
}
